import { Injectable } from '@angular/core';
import { PHOTO_REVIEW_UISTATE } from '../model/photo-review.constant';

@Injectable({ providedIn: 'root' })
export class PhotoUtilsService {
	/**
	 * return a mpped array base just on the metadata information
	 * @param data Photos Data
	 * @param type type of map depend on the tap photo review
	 */
	mapAllPhoto(photos: Array<any>): Array<any> {
		const sortedType = [];
		const photoMap = this.mapPhotos(photos, null);
		photoMap.forEach(p => sortedType.push(p.photoType));
		const groupPhotos = this.groupingMapedPhotos(photoMap);
		const sortingOptionsArray = Array.from(new Set(sortedType));
		return [groupPhotos, sortingOptionsArray];
	}

	/***
	 * return all the sketch photos from api response
	 */
	getGeneralPhotos(photos: Array<any>, photoType: string): Array<any> {
		return this.mapPhotos(photos, null)
			.map(sk => ({
				...sk,
				path: [{ path: sk['path'], bigPath: sk['bigPath'] }]
			}))
			.filter(s => s.photoType === photoType);
	}

	/**
	 * map sketch info from document order
	 * @param proInfo propertie information from order´s document
	 */
	mapSketchInfo(proInfo: Array<any>): any {
		return proInfo.map(sk => {
			const { structureType, units } = sk;
			const levelInfo = units[0].levels.map((l, i) => {
				const info = Object.values(
					l.rooms.reduce((c, v) => {
						c[v.roomType] = c[v.roomType] || [v.roomType, 0];
						c[v.roomType][1]++;
						return c;
					}, {})
				).map(o => ({ room: `${o[1]} ${[o[0]]}` }));
				return {
					level: i + 1,
					info
				};
			});
			return {
				structureType,
				levelInfo
			};
		});
	}

	/**
	 * map exterior info from document order
	 * @param exterior exterior part from order´s document
	 */
	mapExteriorInfo(exterior: Array<any>, path: string): any {
		return exterior.map(e => {
			const exteriorUpdates = this.mapAditionalInfoPhoto(e.exteriorUpdates, path);
			const mechanicalDeficiencies = this.mapAditionalInfoPhoto(e.mechanicalDeficiencies, path);
			const exteriorFeatures = this.mapAditionalInfoPhoto(e.exteriorFeatures, path);
			const exteriorDeficiencies = this.mapAditionalInfoPhoto(e.exteriorDeficiencies, path);
			const photos = this.mapPhotoPath(e.cv_photos, path);
			return {
				exteriorUpdates,
				exteriorFeatures,
				exteriorDeficiencies,
				mechanicalDeficiencies,
				structureType: e.structureType,
				photos
			};
		});
	}

	/**
	 * map interior info from document order
	 * @param interior interior part from order´s document
	 */
	mapInteriorInfo(interior: Array<any>, path: string): any {
		return interior
			.map(e => {
				const { units, structureType } = e;
				const photos = units.map(u => {
					return u.levels
						.map((l, i) => {
							return l.rooms
								.map(r => {
									const interiorDeficiencies = this.mapAditionalInfoPhoto(
										r.interiorDeficiencies,
										path
									);
									const p = this.mapPhotoPath(r.cv_photos, path);
									return p.map(
										photo =>
											(photo = {
												...photo,
												type: r.roomType,
												level: i + 1,
												interiorDeficiencies
											})
									);
								})
								.flat();
						})
						.flat();
				});
				return {
					photos,
					structureType
				};
			})
			.flat();
	}

	/**
	 * return an mapped object with the data ready to use for the photo gallery
	 * @param photos collections of photos
	 */
	setGalleryPhotosInfo(photos: any): Array<any> {
		const pColletion = Object.assign(photos, {});
		return pColletion.map(r => {
			const size = r.path.length;
			r.path = r.path.map((p, i) => {
				if (!p.path) {
					p.path = PHOTO_REVIEW_UISTATE.DEFAULT_IMG;
					p.src = PHOTO_REVIEW_UISTATE.DEFAULT_IMG;
				}
				if (!p.medium && !p.big) {
					return {
						description: `${i + 1}/${size}`,
						medium: p.path,
						big: p.bigPath,
						src: p.path
					};
				}
				return p;
			});
			return r;
		});
	}

	/**
	 * map photo data return from a schema type CV_REVIEW
	 */
	MapCVReviewInfo(photos: any): Array<any> {
		const CVRColletion = Object.assign(photos, {});
		return CVRColletion.map(cv => {
			const path = [
				{
					description: `1`,
					medium: cv.path,
					big: cv.path,
					src: cv.path
				}
			];
			return {
				path,
				photoType: cv.groupName,
				photoDescription: cv.description
			};
		});
	}

	/**
	 * map the url from the cv-photos returned from the document
	 * @param info aditional info photo
	 */
	mapAditionalInfoPhoto(info: any, path: string): Array<any> {
		return info.map(mc => {
			if (mc.cv_photos) {
				const cv_photos = this.mapPhotoPath(mc.cv_photos, path);
				return {
					...mc,
					cv_photos
				};
			}
			return mc;
		});
	}

	/**
	 * map the doc photos into the new array
	 * @param photos array of photos
	 * @param path path to the server
	 */
	private mapPhotoPath(photos: Array<any>, path: string = null): Array<any> {
		const photoMap = this.mapPhotos(photos, path);
		return this.groupingMapedPhotos(photoMap);
	}

	/**
	 * funtion that map an array of photos
	 * @param photos Array of photos returned from the API
	 */
	private mapPhotos(photos: Array<any>, pth: any): Array<any> {
		return photos.map(p => {
			let path;
			let bigPath;
			let pType = '';
			const {
				photoType,
				photoJsonPath,
				photoDescription,
				photoLatitude,
				photoLongitude,
				photoRefJsonPath
			} = p.metadata || p;
			path = p.thumbnails ? p.thumbnails[0] : p.photoFileURL;
			bigPath = p.path;
			if (pth) {
				const expresion = /\s*\/\s*/;
				const photoPath = path.split(expresion);
				const pt = pth.filter(phto => phto.name === photoPath[2]).pop();
				path = pt.thumbnails[0];
				bigPath = pt.path;
			}
			if (photoType) {
				pType = photoType.replace(/_/g, ' ');
			}
			return {
				bigPath,
				path,
				photoType: pType,
				photoJsonPath,
				photoDescription,
				photoLatitude,
				photoLongitude,
				photoRefJsonPath
			};
		});
	}

	/**
	 * group an array of photos by type and path
	 * @param photos mapped photo Array
	 */
	private groupingMapedPhotos(photos: Array<any>): Array<any> {
		return photos
			.reduce((newObject, currentObj, index, array) => {
				const { photoType, photoJsonPath } = currentObj;
				const filteredPhotoPath = array
					.filter(f => f.photoType === photoType && f.photoJsonPath === photoJsonPath)
					.flatMap(r => {
						return {
							path: r.path,
							bigPath: r.bigPath
						};
					});
				if (!newObject[index]) {
					currentObj.path = filteredPhotoPath;
					newObject[index] = [];
				}
				const filterFinal = currentObj.path.findIndex(p => !Array.isArray(p.path));
				if (!filterFinal) {
					newObject[index].push(currentObj);
				}
				return newObject;
			}, [])
			.filter(p => p.length)
			.flat();
	}
}
